import { useEffect, useRef, useState } from 'react';
import { PROCESSING_STATUSES } from '../constants/status';

const FLASH_DURATION_MS = 1500; // 与 animations.css 的 flashOverlay 时长一致

/**
 * 检测状态从"运行中"翻转为终态，返回一次性高亮类名（1.5s 后自动清除）。
 * 首次挂载不播——初始渲染已是 completed 的条目不该闪，这是与轮询共存的关键。
 */
export function useStatusTransition(
  status: string,
  processingStatuses: readonly string[] = PROCESSING_STATUSES,
): string {
  const prevRef = useRef<string | null>(null);
  const [flashClass, setFlashClass] = useState('');

  // 最新集合存 ref、主 effect 只依赖 status：若把 processingStatuses 放进依赖，
  // 调用方传 inline 数组时任意重渲染都会触发 cleanup 清掉 flash timer，
  // 随后 prev === status 提前返回，flashClass 会永久卡在高亮态。
  // 注意声明顺序：此 effect 必须在主 effect 之前，保证同一次提交里先更新集合再判定。
  const processingStatusesRef = useRef(processingStatuses);
  useEffect(() => {
    processingStatusesRef.current = processingStatuses;
  });

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = status;
    if (prev === null || prev === status) return;
    if (!processingStatusesRef.current.includes(prev)) return;

    const cls = status === 'completed' ? 'flash-success'
      : status === 'failed' ? 'flash-error'
      : '';
    if (!cls) return;

    setFlashClass(cls);
    const timer = setTimeout(() => setFlashClass(''), FLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [status]);

  return flashClass;
}
